import { useEffect, useRef, useState } from "react";
import { SearchSelect } from "../ui/SearchSelect";
import { fetchPlayers } from "../../lib/api";
import { readableId } from "./PlayerIdent";
import { useT } from "../../lib/i18n";

/**
 * 挑玩家：搜昵称或 id，选中的排成一行可删的标签。
 *
 * 通用件，任何要「指定一批玩家」的管端功能都可以直接用（发邮件、补发、拉名单……）。
 * 它只做一件事：把 `/players` 的关键词搜索接到 {@link SearchSelect} 的多选上。
 *
 * ## 为什么不是让运营粘一串 id
 *
 * 发邮件那边**曾经是个文本框**，运营把 id 用逗号隔开粘进去，前端按 hex 规则拆一遍、
 * 把不合法的挑出来红字提示「发送时会被拒」。那句提示是假的：提交时只带上了解析成功的
 * 那些，服务端根本没见过坏的那几个，于是邮件**静默地只发给了一部分人**，界面还报成功。
 * 而邮件发出去只能撤回、不能补发给漏掉的人。
 *
 * 从选择器里挑就没有「打错的 id」这种东西——每一项都是搜出来点中的，
 * id 从头到尾没经过人的手，也就没有那条要靠提示文案去兜的路径。
 *
 * 顺带解决另一件事：粘一串十六进制 id 的时候，运营没有任何办法确认这串数字
 * 是不是他要找的那个人。现在选之前看得见昵称。
 */

/** 一次取多少条候选。给多了面板要滚半天，不如让运营把关键词打细 */
const PAGE = 20;

export function PlayerSelect({ value, onChange, labels, disabled, width = 260 }: {
  value: string[];
  /** 第二个参数是选中项的显示名，调用方存下来，切走再切回来标签才不会退成裸 id */
  onChange: (next: string[], labels: Record<string, string>) => void;
  /** 上次记下的 id → 昵称 */
  labels?: Record<string, string>;
  disabled?: boolean;
  width?: number;
}) {
  const t = useT();
  const [opts, setOpts] = useState<Array<{ value: string; label: string; keywords: string }>>([]);
  const [more, setMore] = useState(0);
  const [q, setQ] = useState("");
  /*
   * 「没问到」和「问到了、没有这个人」必须分开。
   *
   * 原先 `.catch` 里直接清空选项，于是接口 500 和这人真不存在画成同一句
   * 「没有匹配」——运营据此判断「这号注销了」，少发一个人，而且发出去补不回来。
   * 跟预览那条是同一个洞，只是后果更重。
   */
  const [err, setErr] = useState<string | null>(null);
  /** 正在取。结果没回来之前列表里还是上一次那一页，得让调用方知道别当真 */
  const [loading, setLoading] = useState(false);
  /** 只认最后一次请求：打字快的时候先发的请求可能后回来，把新结果盖回旧的 */
  const seq = useRef(0);

  useEffect(() => {
    const mine = ++seq.current;
    // 关键词一变**立刻**置 loading，不等 debounce 过去：这 250ms 里列表还是上一次
    // 那一页，不标出来的话回车会取到上一次的第一条
    setLoading(true);
    const timer = setTimeout(() => {
      fetchPlayers({ kw: q.trim() || undefined, size: PAGE })
        .then((r) => {
          if (seq.current !== mine) return;
          setOpts(r.rows.map((p) => ({
            value: p.userIdHex,
            label: p.nickname || readableId(p.userIdHex),
            keywords: readableId(p.userIdHex),
          })));
          setMore(Math.max(0, r.total - r.rows.length));
          setErr(null);
        })
        .catch((e) => { if (seq.current === mine) { setOpts([]); setMore(0); setErr((e as Error).message); } })
        .finally(() => { if (seq.current === mine) setLoading(false); });
    }, 250);
    return () => clearTimeout(timer);
  }, [q]);

  return (
    <SearchSelect
      multiple
      value={value}
      options={opts}
      onChange={onChange}
      knownLabels={labels}
      onQueryChange={setQ}
      placeholder={t("player.select.search")}
      disabled={disabled}
      width={width}
      // 搜到的比这一页多时说一声。不说的话运营会以为「没搜到」= 这人不存在
      hint={more > 0 && !err ? t("player.select.more", { n: more }) : undefined}
      error={err}
      loading={loading}
    />
  );
}
